import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Toast } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { OciService } from '../../services/oci.service';
import { AppFileFolderModel, AppFileModel } from '../../models/appfile.model';
import { AppfileService } from '../../services/appfile.service';
import { AuthService } from '../../core/services/auth.service';
import { AppFeatureStateService } from '../../services/app-feature-state.service';
import { FILES_REPOSITORY_FEATURE_KEYS } from '../../constants/app-menu.constants';

@Component({
  selector: 'app-app-files',
  standalone: true,
  imports: [
    MenuModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    InputTextModule,
    Toast,
    CommonModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './app-files.html',
  styleUrls: ['./app-files.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppFilesComponent implements OnInit, OnDestroy {
  private readonly chunkSizeBytes = 10 * 1024 * 1024;
  private readonly maxChunkUploadRetries = 2;
  private destroy$ = new Subject<void>();

  showAddFileFolderDialog = false;
  showAddFileDialog = false;
  showEditFileNameDialog = false;

  folderList: AppFileFolderModel[] = [];
  filteredFolderList: AppFileFolderModel[] = [];

  appFileFolderModel: AppFileFolderModel = {} as AppFileFolderModel;
  appFileModel: AppFileModel = {} as AppFileModel;
  selectedFolder: AppFileFolderModel | null = null;

  editingFile: AppFileModel | null = null;
  editFileName = '';

  selectedFileIds: Set<number> = new Set();
  selectedFolderIds: Set<number> = new Set();
  collapsedFolders: Set<number> = new Set();

  @ViewChild(FileUpload) fileUpload!: FileUpload;
  selectedFiles: any[] = [];
  isUploadingFiles = false;
  uploadStatuses: Record<string, string> = {};

  actionItems: MenuItem[] = [
    {
      label: 'Delete',
      icon: 'ri-delete-bin-line',
      styleClass: 'danger-menu-item',
      command: () => this.deleteSelected()
    }
  ];

  private readonly viewerSupportedExtensions = new Set([
    'pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dwg', 'dxf'
  ]);

  constructor(
    private service: AppfileService,
    private router: Router,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private ociService: OciService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private appFeatureState: AppFeatureStateService
  ) { }

  // ===== Permission Helpers =====
  canView(): boolean { return this.appFeatureState.canView(FILES_REPOSITORY_FEATURE_KEYS.VIEW); }
  canDelete(): boolean { return this.appFeatureState.canView(FILES_REPOSITORY_FEATURE_KEYS.DELETE); }
  canCreateFolder(): boolean { return this.appFeatureState.canView(FILES_REPOSITORY_FEATURE_KEYS.CREATE_FOLDER); }
  canCreateFile(): boolean { return this.appFeatureState.canView(FILES_REPOSITORY_FEATURE_KEYS.CREATE_FILE); }

  ngOnInit(): void {
    this.getAppFiles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAppFiles(): void {
    this.service.getAppFiles().pipe(takeUntil(this.destroy$)).subscribe({
      next: (folders) => {
        this.folderList = folders;
        this.collapsedFolders = new Set(folders.map(f => f.fileFolderId));
        this.filteredFolderList = [...folders];
        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      }
    });
  }

  // ===== Folder Collapse =====
  toggleFolderCollapse(folderId: number): void {
    if (this.collapsedFolders.has(folderId)) {
      this.collapsedFolders.delete(folderId);
    } else {
      this.collapsedFolders.add(folderId);
    }
  }

  isFolderCollapsed(folderId: number): boolean {
    return this.collapsedFolders.has(folderId);
  }

  // ===== Folder CRUD =====
  addAppFolder(): void {
    if (!this.canCreateFolder()) {
      this.messageService.add({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have permission to create folders.' });
      return;
    }

    const folderName = this.appFileFolderModel.folderName?.trim();
    if (!folderName) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name cannot be empty.' });
      return;
    }
    const duplicate = this.folderList.some(f => f.folderName?.trim().toLowerCase() === folderName.toLowerCase());
    if (duplicate) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name already exists.' });
      return;
    }
    this.service.addAppFileFolder(this.appFileFolderModel).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Folder added successfully.' });
        this.showAddFileFolderDialog = false;
        this.appFileFolderModel = {} as AppFileFolderModel;
        this.getAppFiles();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add folder.' });
      }
    });
  }

  // ===== File Upload =====
  onAddFileToFolder(folder: AppFileFolderModel): void {
    if (!this.canCreateFile()) {
      this.messageService.add({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have permission to create files.' });
      return;
    }

    this.selectedFolder = folder;
    this.appFileModel.fileFolderId = folder.fileFolderId;
    this.selectedFiles = [];
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.showAddFileDialog = true;
  }

  async addAppFile(): Promise<void> {
    if (!this.canCreateFile()) {
      this.messageService.add({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have permission to create files.' });
      return;
    }

    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'No files selected.' });
      return;
    }

    this.isUploadingFiles = true;
    this.selectedFiles.forEach((file: File, index: number) => {
      this.uploadStatuses[this.getUploadStatusKey(file, index)] = 'Pending';
    });
    this.cdr.markForCheck();

    let failedCount = 0;
    for (let index = 0; index < this.selectedFiles.length; index++) {
      const file = this.selectedFiles[index] as File;
      try {
        await this.uploadFileInChunks(file, index);
      } catch {
        failedCount++;
        this.uploadStatuses[this.getUploadStatusKey(file, index)] = 'Failed';
        this.cdr.markForCheck();
      }
    }

    this.isUploadingFiles = false;
    if (failedCount === 0) {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File(s) uploaded successfully.' });
      this.showAddFileDialog = false;
      this.appFileModel = {} as AppFileModel;
      this.selectedFiles = [];
      this.selectedFolder = null;
      this.uploadStatuses = {};
      if (this.fileUpload) {
        this.fileUpload.clear();
      }
      this.getAppFiles();
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `${failedCount} file${failedCount > 1 ? 's' : ''} failed to upload.`
      });
      this.getAppFiles();
      this.cdr.markForCheck();
    }
  }

  private async uploadFileInChunks(file: File, fileIndex: number): Promise<void> {
    const statusKey = this.getUploadStatusKey(file, fileIndex);
    const totalChunks = Math.ceil(file.size / this.chunkSizeBytes);
    const startResponse = await firstValueFrom(this.service.startAppFileChunkUpload({
      fileName: file.name,
      fileSize: file.size,
      fileFolderId: this.selectedFolder?.fileFolderId ?? null,
      chunkSize: this.chunkSizeBytes,
      totalChunks
    }));

    const chunkSize = startResponse.chunkSize || this.chunkSizeBytes;
    for (let chunkIndex = 0; chunkIndex < startResponse.totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      await this.uploadChunkWithRetry(startResponse.uploadId, chunkIndex, chunk);
      const progress = Math.round(((chunkIndex + 1) / startResponse.totalChunks) * 100);
      this.uploadStatuses[statusKey] = `Uploading ${progress}%`;
      this.cdr.markForCheck();
    }

    this.uploadStatuses[statusKey] = 'Finalizing';
    this.cdr.markForCheck();
    await firstValueFrom(this.service.completeAppFileChunkUpload(startResponse.uploadId));
    this.uploadStatuses[statusKey] = 'Uploaded';
    this.cdr.markForCheck();
  }

  private async uploadChunkWithRetry(uploadId: string, chunkIndex: number, chunk: Blob): Promise<void> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= this.maxChunkUploadRetries; attempt++) {
      try {
        await firstValueFrom(this.service.uploadAppFileChunk(uploadId, chunkIndex, chunk));
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  }

  getUploadStatus(file: File, index: number): string {
    return this.uploadStatuses[this.getUploadStatusKey(file, index)] || 'Pending';
  }

  getUploadStatusClass(file: File, index: number): string {
    const status = this.getUploadStatus(file, index);
    if (status === 'Uploaded') return 'upload-preview-status-success';
    if (status === 'Failed') return 'upload-preview-status-error';
    if (status.startsWith('Uploading') || status === 'Finalizing') return 'upload-preview-status-active';
    return 'upload-preview-status-pending';
  }

  closeAddFileDialog(): void {
    if (this.isUploadingFiles) {
      return;
    }
    this.showAddFileDialog = false;
    this.selectedFiles = [];
    this.selectedFolder = null;
    this.appFileModel = {} as AppFileModel;
    this.uploadStatuses = {};
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  private getUploadStatusKey(file: File, index: number): string {
    return `${index}:${file.name}:${file.size}:${file.lastModified}`;
  }

  onFileSelect(event: any): void {
    if (event && event.files) {
      this.selectedFiles = [...this.selectedFiles, ...event.files];
    }
  }

  onFileRemove(event: any): void {
    if (event && event.file) {
      this.selectedFiles = this.selectedFiles.filter(f => f !== event.file);
    }
  }

  onFileClear(): void {
    this.selectedFiles = [];
    this.uploadStatuses = {};
  }

  // ===== Selection =====
  isFileSelected(fileId: number): boolean {
    return this.selectedFileIds.has(fileId);
  }

  toggleFileSelection(fileId: number): void {
    if (this.selectedFileIds.has(fileId)) {
      this.selectedFileIds.delete(fileId);
    } else {
      this.selectedFileIds.add(fileId);
    }
  }

  isFolderFullySelected(folder: AppFileFolderModel): boolean {
    if (!folder.appFileList || folder.appFileList.length === 0) {
      return this.selectedFolderIds.has(folder.fileFolderId);
    }
    return this.selectedFolderIds.has(folder.fileFolderId)
      && folder.appFileList.every(f => this.selectedFileIds.has(f.fileId));
  }

  isFolderPartiallySelected(folder: AppFileFolderModel): boolean {
    if (!folder.appFileList || folder.appFileList.length === 0) return false;
    const someSelected = folder.appFileList.some(f => this.selectedFileIds.has(f.fileId));
    const allSelected = folder.appFileList.every(f => this.selectedFileIds.has(f.fileId));
    return someSelected && !allSelected;
  }

  toggleFolderSelection(folder: AppFileFolderModel): void {
    if (this.isFolderFullySelected(folder)) {
      folder.appFileList?.forEach(f => this.selectedFileIds.delete(f.fileId));
      this.selectedFolderIds.delete(folder.fileFolderId);
    } else {
      folder.appFileList?.forEach(f => this.selectedFileIds.add(f.fileId));
      this.selectedFolderIds.add(folder.fileFolderId);
    }
  }

  get hasSelection(): boolean {
    return this.selectedFileIds.size > 0 || this.selectedFolderIds.size > 0;
  }

  clearSelection(): void {
    this.selectedFileIds.clear();
    this.selectedFolderIds.clear();
  }

  deleteSelected(): void {
    if (!this.hasSelection) return;

    const folderCount = this.selectedFolderIds.size;
    const standaloneFileCount = Array.from(this.selectedFileIds).filter(fileId => {
      return !this.folderList.some(folder =>
        this.selectedFolderIds.has(folder.fileFolderId) &&
        folder.appFileList?.some(f => f.fileId === fileId)
      );
    }).length;

    const parts: string[] = [];
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount > 1 ? 's' : ''}`);
    if (standaloneFileCount > 0) parts.push(`${standaloneFileCount} file${standaloneFileCount > 1 ? 's' : ''}`);

    const cascade = folderCount > 0
      ? ' All files inside the selected folders will also be permanently deleted.'
      : '';

    this.confirmationService.confirm({
      header: 'Delete Items',
      icon: 'ri-error-warning-line',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'confirm-delete-btn',
      message: `Are you sure you want to delete ${parts.join(' and ')}?${cascade} This action cannot be undone.`,
      accept: () => {
        const fileIds = Array.from(this.selectedFileIds);
        const folderIds = Array.from(this.selectedFolderIds);
        this.service.bulkDeleteAppFiles(fileIds, folderIds).subscribe({
          next: (result) => {
            if (result) {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Selected items deleted successfully.' });
            } else {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete selected items.' });
            }
            this.clearSelection();
            this.getAppFiles();
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete selected items.' });
          }
        });
      }
    });
  }

  // ===== File Actions =====
  onEditFileName(file: AppFileModel): void {
    this.editingFile = file;
    this.editFileName = file.fileName || file.fileFullName || '';
    this.showEditFileNameDialog = true;
  }

  saveEditFileName(): void {
    const newName = this.editFileName?.trim();
    if (!newName) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'File name cannot be empty.' });
      return;
    }
    if (!this.editingFile) return;

    const currentUserId = this.authService.getCurrentUser()?.userId || 0;
    const model = { ...this.editingFile, fileName: newName, updatedBy: currentUserId } as AppFileModel;
    this.service.updateAppFile(model).subscribe({
      next: (result) => {
        if (result) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File name updated successfully.' });
          this.getAppFiles();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update file name.' });
        }
        this.showEditFileNameDialog = false;
        this.editingFile = null;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update file name.' });
      }
    });
  }

  onFileClick(file: AppFileModel): void {
    const ext = this.getFileExtension(file.fileName || file.fileFullName || '');
    if (this.viewerSupportedExtensions.has(ext)) {
      this.router.navigate(['/app-files/viewer', file.fileId]);
    } else {
      this.onDownloadFile(file);
    }
  }

  onDownloadFile(file: AppFileModel): void {
    if (!file.filePath) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'File path not available.' });
      return;
    }
    this.ociService.getOciResourceEnhanced(file.filePath).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.fileName || file.fileFullName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file.' });
      }
    });
  }

  onDownloadFolder(folder: AppFileFolderModel): void {
    if (folder.fileFolderId == null || folder.fileFolderId <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Cannot download this folder.' });
      return;
    }
    this.service.downloadFolderAsZip(folder.fileFolderId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${folder.folderName || 'Folder'}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download folder.' });
      }
    });
  }

  onDeleteFile(file: AppFileModel): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${file.fileName || file.fileFullName}"?`,
      accept: () => {
        this.service.deleteAppFile(file.fileId).subscribe({
          next: (result) => {
            if (result) {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File deleted successfully.' });
            } else {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete file.' });
            }
            this.getAppFiles();
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete file.' });
          }
        });
      }
    });
  }

  // ===== File Icons =====
  getFileIcon(file: AppFileModel): string {
    const ext = this.getFileExtension(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return 'ri-file-excel-2-line';
    if (['docx', 'doc'].includes(ext)) return 'ri-file-word-2-line';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'ri-image-line';
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    return 'ri-file-line';
  }

  getFileIconBg(file: AppFileModel): string {
    const ext = this.getFileExtension(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return '#ebf5ec';
    if (['docx', 'doc'].includes(ext)) return '#ecf6fd';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#fff7e0';
    if (ext === 'pdf') return '#fee7e7';
    return '#f4f4f6';
  }

  getFileIconColor(file: AppFileModel): string {
    const ext = this.getFileExtension(file.fileName || file.fileFullName || '');
    if (['xlsx', 'xls'].includes(ext)) return '#1d6f42';
    if (['docx', 'doc'].includes(ext)) return '#2b579a';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#e09500';
    if (ext === 'pdf') return '#eb4747';
    return '#606576';
  }

  getUploadFileIcon(file: File): string {
    const ext = this.getFileExtension(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return 'ri-file-excel-2-line';
    if (['docx', 'doc'].includes(ext)) return 'ri-file-word-2-line';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'ri-image-line';
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    return 'ri-file-line';
  }

  getUploadFileIconBg(file: File): string {
    const ext = this.getFileExtension(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return '#ebf5ec';
    if (['docx', 'doc'].includes(ext)) return '#ecf6fd';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#fff7e0';
    if (ext === 'pdf') return '#fee7e7';
    return '#f4f4f6';
  }

  getUploadFileIconColor(file: File): string {
    const ext = this.getFileExtension(file?.name || '');
    if (['xlsx', 'xls'].includes(ext)) return '#1d6f42';
    if (['docx', 'doc'].includes(ext)) return '#2b579a';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '#e09500';
    if (ext === 'pdf') return '#eb4747';
    return '#606576';
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, unitIndex);
    return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  }

  removeSelectedFile(event: Event, index: number, removeFileCallback: (index: number) => void): void {
    event.stopPropagation();
    if (this.isUploadingFiles) {
      return;
    }
    const removedFile = this.selectedFiles[index];
    removeFileCallback(index);
    this.selectedFiles = this.selectedFiles.filter(file => file !== removedFile);
    this.uploadStatuses = {};
  }

  private getFileExtension(fileName: string): string {
    const name = (fileName || '').trim();
    if (!name.includes('.')) return '';
    return name.split('.').pop()?.toLowerCase() || '';
  }
}
